import React, { ReactNode } from 'react';
import { mb } from '../../common/theme/mbTokens';

// Quill wraps single-paragraph plain text in <p>…</p>. Treat "text" and
// "<p>text</p>" as equivalent for both dirty detection and diff rendering.
export const stripSingleParagraphWrapper = (s: string): string => {
  const trimmed = s.trim();
  const match = trimmed.match(/^<p>([\s\S]*)<\/p>$/);
  if (!match) return trimmed;
  if (match[1].includes('<p>')) return trimmed;
  return match[1];
};

export const htmlValuesDiffer = (a?: string, b?: string): boolean =>
  stripSingleParagraphWrapper(a || '') !== stripSingleParagraphWrapper(b || '');

const htmlToPlainText = (html: string): string => {
  // Convert block-level HTML to newlines, drop everything else.
  return html
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const tokenize = (s: string): string[] => s.split(/(\s+)/).filter(Boolean);

type Chunk = { type: 'equal' | 'add' | 'remove'; text: string };

// Word-level LCS diff. Returns a merged sequence of chunks describing the
// transformation from `before` to `after`. Consecutive same-type chunks are
// coalesced so the rendered output has fewer spans.
const diffWords = (before: string, after: string): Chunk[] => {
  const a = tokenize(before);
  const b = tokenize(after);
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0) as number[]);
  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      if (a[i] === b[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const chunks: Chunk[] = [];
  const push = (c: Chunk) => {
    const last = chunks[chunks.length - 1];
    if (last && last.type === c.type) last.text += c.text;
    else chunks.push(c);
  };
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      push({ type: 'equal', text: a[i] });
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      push({ type: 'remove', text: a[i] });
      i += 1;
    } else {
      push({ type: 'add', text: b[j] });
      j += 1;
    }
  }
  while (i < m) {
    push({ type: 'remove', text: a[i] });
    i += 1;
  }
  while (j < n) {
    push({ type: 'add', text: b[j] });
    j += 1;
  }
  return chunks;
};

const equalStyle: React.CSSProperties = { color: mb.muted, whiteSpace: 'pre-wrap' };
const addStyle: React.CSSProperties = {
  backgroundColor: mb.mint,
  color: mb.green,
  fontWeight: 600,
  whiteSpace: 'pre-wrap',
};
const removeStyle: React.CSSProperties = {
  backgroundColor: mb.dangerSoft,
  color: mb.danger,
  textDecoration: 'line-through',
  whiteSpace: 'pre-wrap',
};

const renderChunks = (chunks: Chunk[], side: 'before' | 'after'): ReactNode => (
  <span style={{ whiteSpace: 'pre-wrap' }}>
    {chunks.map((c, i) => {
      if (c.type === 'equal') {
        return (
          <span key={i} style={equalStyle}>
            {c.text}
          </span>
        );
      }
      if (c.type === 'add' && side === 'after') {
        return (
          <span key={i} style={addStyle}>
            {c.text}
          </span>
        );
      }
      if (c.type === 'remove' && side === 'before') {
        return (
          <span key={i} style={removeStyle}>
            {c.text}
          </span>
        );
      }
      return null;
    })}
  </span>
);

// For rich text values: strip HTML, compute a word-level diff, render each side
// with removed/added tokens highlighted. Keeps common tokens dim so eyes jump
// straight to what changed.
export const renderRichDiffPair = (before?: string, after?: string): { from: ReactNode; to: ReactNode } => {
  const b = htmlToPlainText(before || '');
  const a = htmlToPlainText(after || '');
  if (!b && !a) return { from: '—', to: '—' };
  const chunks = diffWords(b, a);
  return {
    from: b ? renderChunks(chunks, 'before') : '—',
    to: a ? renderChunks(chunks, 'after') : '—',
  };
};

// Kept for short values where a full diff view would be overkill (e.g. still
// rendering raw HTML content inline). Prefer renderRichDiffPair for rich text.
export const renderHtmlDiffValue = (raw?: string): ReactNode => {
  const s = (raw || '').trim();
  if (!s) return '—';
  const inner = stripSingleParagraphWrapper(s);
  return <span dangerouslySetInnerHTML={{ __html: inner }} />;
};

// For plain-text values (labels, titles). Same word-diff highlighting as rich
// text, but no HTML stripping.
export const renderTextDiffPair = (before?: string, after?: string): { from: ReactNode; to: ReactNode } => {
  const b = (before || '').trim();
  const a = (after || '').trim();
  if (!b && !a) return { from: '—', to: '—' };
  const chunks = diffWords(b, a);
  return {
    from: b ? renderChunks(chunks, 'before') : '—',
    to: a ? renderChunks(chunks, 'after') : '—',
  };
};
